import { useTranslation } from "react-i18next";
import { useAppSelector } from "./app/hooks";
import {
  selectAdminAuthority,
  selectCurrentUser,
  selectUserRoles
} from "./features/Users/usersSlice";
import { canUseRotations } from "./features/Auth/adminAccess";
import { AuthNotifications, useAuth } from "./features/Auth/authUtilities";
import { CircularProgress, Typography } from "@mui/material";
import { SignOutButton } from "./common-ui/SignOutButton";
import { performSimplAuthSignOut } from "./features/Auth/signOut";
import { useOptionalSimplAuthCaller } from "./features/Auth/SimplAuthGuard";
import { Outlet } from "react-router-dom";
import { useContext, useEffect, useRef } from "react";
import { BrandingContext } from "./common-ui/BrandingContext";
import { useIdleTimer } from "react-idle-timer";
import { useDispatch } from "react-redux";
import { MessageCenter } from "./features/MessageQueue/MessageCenter";
import { useMessageQueue } from "./features/MessageQueue/useMessageQueue";
import { EulaGate } from "./features/UserInvitations/EulaGate";
import { hasActiveTraineeOrAttendingMembership } from "./app/utils";
// In-app ABP survey (Refiner). Gated server-side via get-user `pendingSurveys`
// + the Firebase `survey_pilot_enabled` flag — no client-side eligibility here.
import { launchSurvey } from "./features/Analytics/Refiner";
import { selectFeatureFlags } from "./features/Firebase/firebaseSlice";

const INACTIVITY_TIMEOUT_SECONDS = 60 * 15;

export const DefaultHome = () => {
  const { t } = useTranslation();
  // `<SimplAuthGuard>` provides the caller once the session probe
  // settles on `authed`. We use the optional variant so this component
  // can still mount in the `ENABLE_AUTH = false` escape hatch and in
  // tests that do not wrap in a guard.
  const ctx = useOptionalSimplAuthCaller();
  const caller = ctx?.caller ?? null;
  const { status: authStatus, error: authError } = useAuth({ caller });
  const branding = useContext(BrandingContext);
  const dispatch = useDispatch();
  const { hasBlocking } = useMessageQueue();

  const currentAuthenticatedUser = useAppSelector(selectCurrentUser);
  const roles = useAppSelector(selectUserRoles);
  const adminAuthority = useAppSelector(selectAdminAuthority);
  const featureFlags = useAppSelector(selectFeatureFlags);

  const necessaryStatuses = [authStatus, branding.status];

  useIdleTimer({
    crossTab: true,
    syncTimers: 1000,
    timeout: 1000 * INACTIVITY_TIMEOUT_SECONDS,
    onIdle: () => {
      if (currentAuthenticatedUser) {
        void performSimplAuthSignOut({ dispatch });
      }
    },
    debounce: 1000
  });

  // In-app ABP survey (Refiner). Server-driven gate, mirroring iOS/Android:
  // show only when the Firebase kill-switch is on AND the server's
  // `pendingSurveys` still contains the key (= eligible ABP clinician who hasn't
  // completed it). The audience/eligibility + completion suppression both live
  // server-side in get-user, so there is no client-side eligibility logic here.
  const ABP_SPONSOR_CODE = "ABP";
  const SURVEY_KEY = "abp_end_user_pilot";
  const surveyFiredRef = useRef(false);
  useEffect(() => {
    if (!currentAuthenticatedUser || surveyFiredRef.current) return;
    if (!featureFlags.survey_pilot_enabled) return;

    // pendingSurveys is returned by get-user (passed through at runtime) but not
    // on the generated GraphQL User type, so read it loosely.
    const user = currentAuthenticatedUser as unknown as {
      pendingSurveys?: string[];
    };
    if (!user.pendingSurveys?.includes(SURVEY_KEY)) return;

    // Hidden-field IDs for the response. roleId = programRoleId (canonical role
    // UUID). networks/sponsor aren't strongly typed on the runtime object, so
    // traverse loosely and handle both the REST array and GraphQL {items} shape.
    const memberships = (currentAuthenticatedUser.programMemberships?.items ??
      []) as any[];
    const clinicianMembership =
      memberships.find(
        (m) =>
          m &&
          !m.endDate &&
          (m.programRole === "Trainee" || m.programRole === "Attending")
      ) ?? memberships[0];
    const networks = (clinicianMembership?.program?.networks?.items ??
      clinicianMembership?.program?.networks ??
      []) as any[];
    const abpNetwork =
      networks.find(
        (n) =>
          (n?.sponsor?.name ?? n?.network?.sponsor?.name ?? "").toUpperCase() ===
          ABP_SPONSOR_CODE
      ) ?? networks[0];

    surveyFiredRef.current = true;
    launchSurvey(
      {
        userId: currentAuthenticatedUser.id ?? "",
        programId: clinicianMembership?.programId ?? "",
        networkId: abpNetwork?.id ?? abpNetwork?.network?.id ?? "",
        roleId: clinicianMembership?.programRoleId ?? ""
      },
      {
        projectId: featureFlags.refiner_project_id,
        formUuid: featureFlags.refiner_form_uuid_web
      }
    );
  }, [currentAuthenticatedUser, featureFlags.survey_pilot_enabled]);

  if (necessaryStatuses.every((status) => status === "idle")) {
    return <Typography variant="h2">{t("Awaiting data...")}</Typography>;
  }

  if (necessaryStatuses.includes("loading")) {
    return <CircularProgress />;
  }

  if (authStatus === "failed") {
    return (
      <>
        <Typography variant="h2">{t("Error")}</Typography>
        <Typography variant="h3">
          {t(AuthNotifications.FetchUserError)}
        </Typography>
        {authError && <Typography>{authError}</Typography>}
      </>
    );
  }

  // If the user is logged in but doesn't have an associated user in the database,
  // show an appropriate error.
  if (authStatus === "succeeded" && !currentAuthenticatedUser) {
    return (
      <div>
        {t(AuthNotifications.NoDDBUser)}
        <SignOutButton />
      </div>
    );
  }

  if (necessaryStatuses.every((status) => status === "succeeded")) {
    // Render the layout for anyone who has either an admin role
    // (SystemAdmin / ProgramAdmin), an active Trainee / Attending
    // program membership, or a rotation they direct (an RD-only
    // faculty member gets the limited Rotations view). Users with
    // none (e.g. an authenticated record that only carries an
    // institutional Admin membership, or a stale account with no
    // active memberships) fall through to the "no permissions" message
    // so they aren't dropped on a blank screen with no navigation
    // options.
    const hasAdminRole = !!roles && roles.length > 0;
    const canAssess = hasActiveTraineeOrAttendingMembership(
      currentAuthenticatedUser
    );
    if (hasAdminRole || canAssess || canUseRotations(adminAuthority)) {
      return (
        <EulaGate>
          <MessageCenter />
          {!hasBlocking && <Outlet />}
        </EulaGate>
      );
    }

    return (
      <div>
        {t("Current user has no permissions.")}
        <SignOutButton />
      </div>
    );
  }

  return null;
};
